/** @format */

const ISO_DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const HAS_TIMEZONE_REGEX = /(Z|[+-]\d{2}:\d{2})$/i;
const LEGACY_MM_DD_YYYY_REGEX = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

function parseUtcMsInternal(dateString: unknown): number | null {
  if (typeof dateString !== 'string') {
    return null;
  }

  const normalizedDateString = dateString.trim();

  if (!normalizedDateString) {
    return null;
  }

  // Date-only values (e.g. `2026-12-31` from Cart/SC5 rewards) carry no time
  // component. The athlete can use the reward for the whole day, so treat them
  // as the end of that local day (23:59:59.999). Parsing them as UTC midnight
  // would land on the previous local calendar day in US time zones, and the
  // last-full-day rollback would then move the display back one more day.
  if (ISO_DATE_ONLY_REGEX.test(normalizedDateString)) {
    const year = Number(normalizedDateString.slice(0, 4));
    const month = Number(normalizedDateString.slice(5, 7));
    const day = Number(normalizedDateString.slice(8, 10));
    const localEndOfDay = new Date(year, month - 1, day, 23, 59, 59, 999);
    // Reject impossible dates (e.g. 2026-02-30) that would silently roll over.
    const isValid =
      localEndOfDay.getFullYear() === year &&
      localEndOfDay.getMonth() === month - 1 &&
      localEndOfDay.getDate() === day;
    return isValid ? localEndOfDay.getTime() : null;
  }

  // Legacy `MM/DD/YYYY` (or `M/D/YYYY`) values carry no time component. The
  // athlete can use the reward for the whole day, so treat them as the end of
  // that local day (23:59:59.999). The last-full-day rule then keeps the date
  // as-is, preserving the old ExpirationDatePipe "display as-is" behavior.
  const legacyMatch = LEGACY_MM_DD_YYYY_REGEX.exec(normalizedDateString);
  if (legacyMatch) {
    const month = Number(legacyMatch[1]);
    const day = Number(legacyMatch[2]);
    const year = Number(legacyMatch[3]);
    const localEndOfDay = new Date(year, month - 1, day, 23, 59, 59, 999);
    // Reject impossible dates (e.g. 13/40/2026) that would silently roll over.
    const isValid =
      localEndOfDay.getFullYear() === year &&
      localEndOfDay.getMonth() === month - 1 &&
      localEndOfDay.getDate() === day;
    return isValid ? localEndOfDay.getTime() : null;
  }

  const dateToParse = HAS_TIMEZONE_REGEX.test(normalizedDateString)
    ? normalizedDateString
    : `${normalizedDateString}Z`;

  const utcMs = Date.parse(dateToParse);
  return Number.isNaN(utcMs) ? null : utcMs;
}

/**
 * Parses a date string into UTC milliseconds, treating naive (timezone-less)
 * values as UTC, and date-only (`YYYY-MM-DD`) and legacy `MM/DD/YYYY` values
 * as the end of that local day. Returns null when the input is not a
 * parseable date.
 */
export function parseToUtcMs(dateString: unknown): number | null {
  return parseUtcMsInternal(dateString);
}

/**
 * Resolves the last full local calendar day the athlete can use the reward.
 * Returns the original expiration date as-is when it expires today, or exactly
 * at 23:59:59 local time; otherwise rolls back to 23:59:59.000 of the previous
 * local day.
 *
 * This is the "last full day" display logic shared by My Account and Cart so
 * both surfaces show the same expiration date to the athlete.
 *
 * `now` is the reference "current" date used to decide whether the expiration
 * is "today". It defaults to the real current time, but callers that need a
 * stable, cacheable result (e.g. a pure pipe) can pass an explicit value —
 * typically the start of the current local day — so the output only changes
 * when the local day actually changes.
 */
export function getRewardDisplayExpirationDate(
  dateString: string | null | undefined,
  now: Date = new Date(),
): Date | null {
  const utcMs = parseToUtcMs(dateString);

  if (utcMs === null) {
    return null;
  }

  const originalExpirationDate = new Date(utcMs);

  const isSameLocalDay =
    originalExpirationDate.getFullYear() === now.getFullYear() &&
    originalExpirationDate.getMonth() === now.getMonth() &&
    originalExpirationDate.getDate() === now.getDate();

  const isEndOfLocalDay =
    originalExpirationDate.getHours() === 23 &&
    originalExpirationDate.getMinutes() === 59 &&
    originalExpirationDate.getSeconds() === 59;

  if (isSameLocalDay || isEndOfLocalDay) {
    return originalExpirationDate;
  }

  const displayExpirationDate = new Date(originalExpirationDate);
  displayExpirationDate.setDate(displayExpirationDate.getDate() - 1);
  displayExpirationDate.setHours(23, 59, 59, 0);

  return displayExpirationDate;
}
